import React, { useState, useEffect, useRef } from "react";
import images from "../../assets/images";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ROUTES } from "../../constants/routes.constants";
import userService from "../../services/user.service";
import { useDispatch } from "react-redux";
import { userSlice } from "../../redux/slices/user.slice";
import {
  buildCodeLoginContextFromUser,
  saveCodeLoginContext,
} from "../../utility/code-login-context";
import {
  applyGuestProfileToLogin,
  getGuestProfileLocal,
  saveGuestProfileLocal,
} from "../../utility/guest-profile";

const STEP = {
  CODE: "code",
  CHOICE: "choice",
  GUEST_PROFILE: "guest_profile",
};

const emptyGuestProfile = {
  firstname: "",
  lastname: "",
  licenseNumber: "",
};

const LoginWithCodePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoLoginAttempted = useRef(false);

  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(STEP.CODE);
  const [pendingLogin, setPendingLogin] = useState(null);
  const [guestProfile, setGuestProfile] = useState(emptyGuestProfile);

  const persistContext = (loginData, loginCode) => {
    const context = buildCodeLoginContextFromUser(
      loginData?.user,
      loginCode || loginData?.user?.code
    );
    saveCodeLoginContext(context);
    return context;
  };

  const loginWithCode = async (loginCode) => {
    if (!loginCode) {
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const loginResult = await userService.LoginWithCode(loginCode);
      if (loginResult.error === null && loginResult.data) {
        persistContext(loginResult.data, loginCode);
        setPendingLogin(loginResult.data);
        setStep(STEP.CHOICE);
      } else {
        setError("Invalid or expired code. Please try again.");
      }
    } catch (err) {
      console.error("Login with code error:", err);
      setError("Unable to log in with this code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitCode = async (event) => {
    event.preventDefault();
    await loginWithCode(code?.trim());
  };

  const handleContinueAsGuest = () => {
    setError("");
    const savedProfile = getGuestProfileLocal();
    if (savedProfile) {
      setGuestProfile({
        firstname: savedProfile.firstname || "",
        lastname: savedProfile.lastname || "",
        licenseNumber: savedProfile.licenseNumber || "",
      });
    }
    setStep(STEP.GUEST_PROFILE);
  };

  const handleLoginRegister = () => {
    // Do not leave a guest session active
    dispatch(userSlice.actions.logout());
    setPendingLogin(null);
    navigate(ROUTES.LOGIN_ROUTE, {
      replace: true,
      state: { fromCodeLogin: true },
    });
  };

  const handleGuestProfileSubmit = (event) => {
    event.preventDefault();
    if (!pendingLogin) {
      return;
    }

    const firstname = guestProfile.firstname?.trim();
    const lastname = guestProfile.lastname?.trim();
    const licenseNumber = guestProfile.licenseNumber?.trim();

    if (!firstname || !lastname || !licenseNumber) {
      setError("First name, last name, and licence number are required.");
      return;
    }

    setError("");
    const profile = { firstname, lastname, licenseNumber };
    saveGuestProfileLocal(profile);

    const loginWithProfile = applyGuestProfileToLogin(pendingLogin, profile);
    dispatch(userSlice.actions.login(loginWithProfile));
    persistContext(
      loginWithProfile,
      code?.trim() || pendingLogin?.user?.code
    );
    navigate(ROUTES.CODE_NOTIFICATION_ROUTE, { replace: true });
  };

  useEffect(() => {
    document.title = "TransferMyRx::Login with code";
  }, []);

  useEffect(() => {
    const codeFromUrl = searchParams.get("code")?.trim();
    if (!codeFromUrl || autoLoginAttempted.current) {
      return;
    }

    autoLoginAttempted.current = true;
    setCode(codeFromUrl);
    loginWithCode(codeFromUrl);
  }, [searchParams]);

  return (
    <>
      {isLoading && (
        <div className="card w-100 h-100 d-flex justify-content-center align-items-center position-absolute bg-transparent loading-container">
          <div className="w-100 h-100 position-absolute bg-white opacity-75"></div>
          <div
            className={`d-flex justify-content-flex-start align-items-center position-absolute p-3
               border border-dark shadow-lg rounded-1 loading animate__animated animate__faster
               animate__zoomIn`}
          >
            <div className="spinner-border text-success me-3"></div>
            <span className="">Processing...</span>
          </div>
        </div>
      )}
      <div
        id="login-with-code-form"
        className="auth-form p-4 p-sm-5 shadow-lg rounded-1 border-primary bg-white animate__animated"
      >
        <div className="text-center mb-3">
          <a href="https://www.TransferMyRx.com">
            <img src={images.Logo} alt="TransferMyRx" className="img-fluid" />
          </a>
        </div>

        {step === STEP.CODE && (
          <>
            <h5 className="text-center mb-4">Login With Code</h5>
            <form onSubmit={handleSubmitCode}>
              <div className="col-12 mb-3">
                <label className="form-label" htmlFor="code-txt">
                  Code
                </label>
                <input
                  id="code-txt"
                  type="text"
                  className="form-control"
                  name="code"
                  placeholder="Code"
                  value={code}
                  disabled={isLoading}
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>

              {error && (
                <div className="alert alert-danger py-2" role="alert">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-100 btn btn-default btn-lg"
                disabled={isLoading || !code?.trim()}
              >
                Log In
              </button>
            </form>
            <div className="new-account mt-3">
              Already have account?&nbsp;
              <Link
                id="back-to-login"
                className="link-default"
                to={ROUTES.LOGIN_ROUTE}
              >
                Log In With Account
              </Link>
            </div>
          </>
        )}

        {step === STEP.CHOICE && (
          <>
            <h5 className="text-center mb-3">How would you like to continue?</h5>
            <p className="text-center text-muted mb-4">
              You can continue as a guest for this notification, or log in /
              register with TransferMyRx.
            </p>
            {error && (
              <div className="alert alert-danger py-2" role="alert">
                {error}
              </div>
            )}
            <button
              type="button"
              className="w-100 btn btn-default btn-lg mb-3"
              disabled={isLoading}
              onClick={handleContinueAsGuest}
            >
              Continue as guest
            </button>
            <button
              type="button"
              className="w-100 btn btn-outline-secondary btn-lg"
              disabled={isLoading}
              onClick={handleLoginRegister}
            >
              Login/Register to TransferMyRx
            </button>
          </>
        )}

        {step === STEP.GUEST_PROFILE && (
          <>
            <h5 className="text-center mb-3">Your details</h5>
            <p className="text-center text-muted mb-4">
              Please enter your name and licence number to continue.
            </p>
            <form onSubmit={handleGuestProfileSubmit}>
              <div className="mb-3">
                <label className="form-label" htmlFor="guest-firstname">
                  First name
                </label>
                <input
                  id="guest-firstname"
                  type="text"
                  className="form-control"
                  value={guestProfile.firstname}
                  disabled={isLoading}
                  onChange={(event) =>
                    setGuestProfile((prev) => ({
                      ...prev,
                      firstname: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="guest-lastname">
                  Last name
                </label>
                <input
                  id="guest-lastname"
                  type="text"
                  className="form-control"
                  value={guestProfile.lastname}
                  disabled={isLoading}
                  onChange={(event) =>
                    setGuestProfile((prev) => ({
                      ...prev,
                      lastname: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="guest-license">
                  Licence number
                </label>
                <input
                  id="guest-license"
                  type="text"
                  className="form-control"
                  value={guestProfile.licenseNumber}
                  disabled={isLoading}
                  onChange={(event) =>
                    setGuestProfile((prev) => ({
                      ...prev,
                      licenseNumber: event.target.value,
                    }))
                  }
                />
              </div>

              {error && (
                <div className="alert alert-danger py-2" role="alert">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-100 btn btn-default btn-lg mb-2"
                disabled={
                  isLoading ||
                  !guestProfile.firstname?.trim() ||
                  !guestProfile.lastname?.trim() ||
                  !guestProfile.licenseNumber?.trim()
                }
              >
                Continue
              </button>
              <button
                type="button"
                className="w-100 btn btn-link"
                disabled={isLoading}
                onClick={() => {
                  setError("");
                  setStep(STEP.CHOICE);
                }}
              >
                Back
              </button>
            </form>
          </>
        )}
      </div>
    </>
  );
};

export default LoginWithCodePage;
